import { Ref, computed, ref } from "vue";
import { Lottery } from "@sazka/lotteries/js/models/lottery";
import { LotteryParser } from "@sazka/lotteries/js/utils/lottery-parser";
import { getDrawTime, getDrawTimeBulk } from "./lottery-countdown-api";

export interface CountdownRef {
    lottery: Lottery;
    secondsRemaining: number;
}

const countdownRefs: Ref<CountdownRef[]> = ref([]);
const subscribedLotteries = ref<Lottery[]>([]);
const subscribedLotteriesSet = computed(() => new Set(subscribedLotteries.value)); // unique lotteries - represent active countdowns
let tickingIntervalHandle: ReturnType<typeof setInterval> = null;

/**
 * Gets the countdown ref for the lottery;
 * creates one if it doesn't exist
 *
 * @param lottery
 * @returns the countdown ref for the lottery
 */
function getCountdownRef(lottery: Lottery) {
    let cdRef: CountdownRef = countdownRefs.value.find((x) => x.lottery === lottery);

    if (!cdRef) {
        cdRef = { lottery, secondsRemaining: 0 };
        countdownRefs.value.push(cdRef);
    }

    return cdRef;
}

/**
 * Gets the countdown refs for the lotteries;
 * it does not create refs for lotteries that don't have one
 *
 * @param lotteries
 * @returns the countdown refs for the lotteries
 */
function getCountdownRefs(lotteries: Lottery[]): CountdownRef[] {
    return countdownRefs.value.filter((x) => lotteries.includes(x.lottery));
}

/**
 * Sets the countdown ref for the lottery
 *
 * @param lottery
 * @param seconds
 * @returns the countdown ref for the lottery
 */
function setCountdownRef(lottery: Lottery, seconds: number) {
    const cdRef = getCountdownRef(lottery);
    cdRef.secondsRemaining = seconds;

    return cdRef;
}

async function requestLotteryDrawTimes(lotteries: Lottery[]) {
    if (lotteries.length > 1) {
        (await getDrawTimeBulk(lotteries)).forEach((response) => {
            setCountdownRef(LotteryParser.parseLotteryName(response.lottery), response.remainingSeconds);
        });
    } else if (lotteries.length === 1) {
        const data = await getDrawTime(lotteries[0]);
        setCountdownRef(LotteryParser.parseLotteryName(data.lottery), data.remainingSeconds);
    }
}

/**
 * Ticks the countdowns;
 * If a countdown reaches 0, it requests the new draw times in bulk
 */
async function tick() {
    if (!subscribedLotteriesSet.value.size) {
        stopTicking();
        return;
    }

    const lotteriesToRequest: Lottery[] = [];

    for (const lottery of subscribedLotteriesSet.value) {
        const cdRef = getCountdownRef(lottery);

        if (cdRef.secondsRemaining <= 0) {
            lotteriesToRequest.push(lottery);
        } else {
            cdRef.secondsRemaining = cdRef.secondsRemaining - 1;
        }
    }

    if (lotteriesToRequest.length) {
        await requestLotteryDrawTimes(lotteriesToRequest);
    }
}

async function startTicking() {
    if (tickingIntervalHandle) return;

    await tick();
    tickingIntervalHandle = setInterval(tick, 1000);
}

function stopTicking() {
    if (!tickingIntervalHandle) return;

    clearInterval(tickingIntervalHandle);
}

let initialLoadPromise: Promise<void> = null;

const handleWindowBlur = () => {
    let onBlurTime: number;

    window.addEventListener("blur", () => {
        onBlurTime = Date.now();
    });

    window.addEventListener("visibilitychange", () => {
        if (!onBlurTime || Date.now() - onBlurTime < 10_000) return;

        const lotteriesToRequest = [...subscribedLotteriesSet.value];
        if (lotteriesToRequest.length) {
            requestLotteryDrawTimes(lotteriesToRequest);
        }
    });
};

/**
 * Creates a manager for the lottery countdowns;
 * it keeps track of the countdowns for the lotteries
 *
 * @param lotteries
 * @returns the manager
 */
export const useLotteryCountdownManager = (lotteries: Lottery[]) => {
    let hasSubscribed = false;
    const subscribingLotteries = []; // array to keep track of subscribing lotteries of the current manager `instance`

    const addLottery = (lottery: Lottery) => {
        if (!subscribedLotteriesSet.value.has(lottery)) {
            setCountdownRef(lottery, 0);
        }
        subscribedLotteries.value.push(lottery);
        subscribingLotteries.push(lottery);
    };

    if (lotteries.length) {
        subscribingLotteries.push(...lotteries);
        const newLotteries = lotteries.filter((lottery) => !subscribedLotteriesSet.value.has(lottery));
        for (const lottery of newLotteries) {
            setCountdownRef(lottery, 0);
        }

        subscribedLotteries.value.push(...lotteries);

        hasSubscribed = true;
    }

    if (hasSubscribed && !tickingIntervalHandle && !initialLoadPromise) {
        initialLoadPromise = startTicking();
    }

    const countdowns = computed<CountdownRef[]>(() => getCountdownRefs(lotteries));

    handleWindowBlur();

    return {
        addLottery,
        countdowns,
        getCountdownRef,
        initialLoadPromise,
        unsubscribe: () => {
            if (!hasSubscribed) return;

            for (const lottery of subscribingLotteries) {
                const index = subscribedLotteries.value.indexOf(lottery);
                if (index > -1) {
                    subscribedLotteries.value.splice(index, 1);
                }
            }

            hasSubscribed = false;
        },
    };
};
